import React, { useState, useEffect } from 'react';
import FormModal from '../../Songs/components/FormModal';
import { apiClient } from '../../../api/apiClient';
import { CREATE_E_ACCOUNT_URL, UPDATE_E_ACCOUNT_URL } from '../../../api/eAccount';
import { toast } from '../../../components/shared/toast';

function countWords(str) {
  if (!str || typeof str !== 'string') return 0;
  return str.trim().split(/\s+/).filter(Boolean).length;
}

function isValidEmail(email) {
  if (!email) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

function CreateEAccountModal({ isOpen, onClose, onSuccess, initialType = '', accountToEdit = null }) {
  const isEditing = !!accountToEdit;
  const [accountType, setAccountType] = useState(
    accountToEdit?.account_type || initialType
  );

  const formatDateForInput = (d) => {
    if (!d) return '';
    return String(d).split('T')[0];
  };

  const [formData, setFormData] = useState({
    emailName: accountToEdit?.email_name || '',
    recoveryPhone: accountToEdit?.recovery_phone || '',
    recoveryEmail: accountToEdit?.recovery_email || '',
    name: accountToEdit?.name || '',
    whyBuy: accountToEdit?.why_buy || '',
    accountEmail: accountToEdit?.account_email || '',
    socialType: accountToEdit?.social_type || 'YouTube',
    subscriptionFor: accountToEdit?.subscription_for || '',
    mail: accountToEdit?.mail || '',
    renewDate: formatDateForInput(accountToEdit?.renew_date),
    description: accountToEdit?.description || '',
    whoHas: accountToEdit?.who_has || ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (accountToEdit) {
      setAccountType(accountToEdit.account_type || initialType);
      setFormData({
        emailName: accountToEdit.email_name || '',
        recoveryPhone: accountToEdit.recovery_phone || '',
        recoveryEmail: accountToEdit.recovery_email || '',
        name: accountToEdit.name || '',
        whyBuy: accountToEdit.why_buy || '',
        accountEmail: accountToEdit.account_email || '',
        socialType: accountToEdit.social_type || 'YouTube',
        subscriptionFor: accountToEdit.subscription_for || '',
        mail: accountToEdit.mail || '',
        renewDate: accountToEdit.renew_date ? String(accountToEdit.renew_date).split('T')[0] : '',
        description: accountToEdit.description || '',
        whoHas: accountToEdit.who_has || ''
      });
    }
  }, [accountToEdit, initialType]);

  const handleTypeChange = (e) => {
    const newType = e.target.value;
    setAccountType(newType);
    setFormData(prev => ({
      ...prev,
      emailName: '',
      recoveryPhone: '',
      recoveryEmail: '',
      name: '',
      whyBuy: '',
      accountEmail: '',
      socialType: 'YouTube',
      subscriptionFor: '',
      mail: '',
      renewDate: '',
      description: '',
      whoHas: ''
    }));
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validate = () => {
    if (!accountType) {
      toast.warning('Account Type is required.');
      return false;
    }

    if (accountType === 'email') {
      if (!formData.emailName.trim()) {
        toast.warning('Email Name is required.');
        return false;
      }
      if (!formData.recoveryPhone.trim()) {
        toast.warning('Recovery Phone Number is required.');
        return false;
      }
      if (!formData.recoveryEmail.trim()) {
        toast.warning('Recovery Email is required.');
        return false;
      }
      if (!isValidEmail(formData.recoveryEmail)) {
        toast.warning('Please enter a valid Recovery Email address.');
        return false;
      }
    } else if (accountType === 'domain' || accountType === 'server') {
      const fieldLabel = accountType === 'domain' ? 'Domain Name' : 'Server Name';
      if (!formData.name.trim()) {
        toast.warning(`${fieldLabel} is required.`);
        return false;
      }
      if (!formData.whyBuy.trim()) {
        toast.warning('Why was buy is required.');
        return false;
      }
      if (!formData.accountEmail.trim()) {
        toast.warning('Account Email is required.');
        return false;
      }
      if (!isValidEmail(formData.accountEmail)) {
        toast.warning('Please enter a valid Account Email address.');
        return false;
      }
      if (!formData.renewDate) {
        toast.warning('Renew Date is required.');
        return false;
      }
    } else if (accountType === 'social_account') {
      if (!formData.socialType) {
        toast.warning('Social Account Type is required.');
        return false;
      }
      if (!formData.name.trim()) {
        toast.warning('Name is required.');
        return false;
      }
      if (!formData.mail.trim()) {
        toast.warning('Mail is required.');
        return false;
      }
      if (!isValidEmail(formData.mail)) {
        toast.warning('Please enter a valid Mail address.');
        return false;
      }
    } else if (accountType === 'subscription') {
      if (!formData.subscriptionFor.trim()) {
        toast.warning('Subscription For is required.');
        return false;
      }
      if (!formData.name.trim()) {
        toast.warning('Name is required.');
        return false;
      }
      if (!formData.mail.trim()) {
        toast.warning('Mail is required.');
        return false;
      }
      if (!isValidEmail(formData.mail)) {
        toast.warning('Please enter a valid Mail address.');
        return false;
      }
      if (!formData.renewDate) {
        toast.warning('Renew Date is required.');
        return false;
      }
    }

    if (!formData.description.trim()) {
      toast.warning('Description is required.');
      return false;
    }

    const words = countWords(formData.description);
    if (words > 350) {
      toast.warning(`Description cannot exceed 350 words. Currently: ${words} words.`);
      return false;
    }

    if (!formData.whoHas.trim()) {
      toast.warning('Who Has is required.');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setIsSubmitting(true);

      const payload = {
        account_type: accountType,
        description: formData.description.trim(),
        who_has: formData.whoHas.trim()
      };

      if (accountType === 'email') {
        payload.email_name = formData.emailName.trim();
        payload.recovery_phone = formData.recoveryPhone.trim();
        payload.recovery_email = formData.recoveryEmail.trim();
      } else if (accountType === 'domain' || accountType === 'server') {
        payload.name = formData.name.trim();
        payload.why_buy = formData.whyBuy.trim();
        payload.account_email = formData.accountEmail.trim();
        payload.renew_date = formData.renewDate;
      } else if (accountType === 'social_account') {
        payload.social_type = formData.socialType;
        payload.name = formData.name.trim();
        payload.mail = formData.mail.trim();
      } else if (accountType === 'subscription') {
        payload.subscription_for = formData.subscriptionFor.trim();
        payload.name = formData.name.trim();
        payload.mail = formData.mail.trim();
        payload.renew_date = formData.renewDate;
      }

      if (isEditing) {
        const res = await apiClient(UPDATE_E_ACCOUNT_URL(accountToEdit.id), {
          method: 'PUT',
          body: payload
        });
        toast.success('E-Account updated successfully');
        if (onSuccess) onSuccess(res.account || payload);
      } else {
        const res = await apiClient(CREATE_E_ACCOUNT_URL, {
          method: 'POST',
          body: payload
        });
        toast.success('E-Account created successfully');
        if (onSuccess) onSuccess(res.account || payload);
      }

      onClose();
    } catch (err) {
      console.error('Error saving E-Account:', err);
      toast.warning(err?.message || `Failed to ${isEditing ? 'update' : 'create'} E-Account`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const wordCount = countWords(formData.description);

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? 'Edit E-Account' : 'Create E-Account'}
      className="modal-wide"
    >
      <form onSubmit={handleSubmit} className="e-account-modal-form">
        {/* FIRST FIELD - ACCOUNT TYPE DROPDOWN */}
        <div className="form-group-item" style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#0f172a', marginBottom: '6px' }}>
            Account Type <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <select
            value={accountType}
            onChange={handleTypeChange}
            style={{
              width: '100%',
              padding: '10px 14px',
              borderRadius: '8px',
              border: '1px solid #cbd5e1',
              fontSize: '14px',
              color: '#0f172a',
              outline: 'none',
              backgroundColor: '#ffffff'
            }}
          >
            <option value="" disabled>Select Account Type...</option>
            <option value="email">Email</option>
            <option value="domain">Domain</option>
            <option value="server">Server</option>
            <option value="social_account">Social Account</option>
            <option value="subscription">Subscription</option>
          </select>
        </div>

        {/* DYNAMIC FIELDS - ONLY APPEAR AFTER ACCOUNT TYPE IS SELECTED */}
        {accountType === 'email' && (
          <>
            <div className="form-grid-two-col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#0f172a', marginBottom: '6px' }}>
                  Email Name <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="text"
                  placeholder="Enter email name..."
                  value={formData.emailName}
                  onChange={(e) => handleInputChange('emailName', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#0f172a', marginBottom: '6px' }}>
                  Recovery Phone Number <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="text"
                  placeholder="Enter recovery phone..."
                  value={formData.recoveryPhone}
                  onChange={(e) => handleInputChange('recoveryPhone', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#0f172a', marginBottom: '6px' }}>
                Recovery Email <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="text"
                placeholder="Enter recovery email..."
                value={formData.recoveryEmail}
                onChange={(e) => handleInputChange('recoveryEmail', e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  borderRadius: '8px',
                  border: '1px solid #cbd5e1',
                  fontSize: '14px'
                }}
              />
            </div>
          </>
        )}

        {(accountType === 'domain' || accountType === 'server') && (
          <>
            <div className="form-grid-two-col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#0f172a', marginBottom: '6px' }}>
                  {accountType === 'domain' ? 'Domain Name' : 'Server Name'} <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="text"
                  placeholder={`Enter ${accountType === 'domain' ? 'domain' : 'server'} name...`}
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#0f172a', marginBottom: '6px' }}>
                  Why was buy <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="text"
                  placeholder="Reason for purchase..."
                  value={formData.whyBuy}
                  onChange={(e) => handleInputChange('whyBuy', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>

            <div className="form-grid-two-col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#0f172a', marginBottom: '6px' }}>
                  Account Email <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="text"
                  placeholder="Enter account email..."
                  value={formData.accountEmail}
                  onChange={(e) => handleInputChange('accountEmail', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#0f172a', marginBottom: '6px' }}>
                  Renew Date <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="date"
                  value={formData.renewDate}
                  onChange={(e) => handleInputChange('renewDate', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>
          </>
        )}

        {accountType === 'social_account' && (
          <>
            <div className="form-grid-two-col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#0f172a', marginBottom: '6px' }}>
                  Account Type <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <select
                  value={formData.socialType}
                  onChange={(e) => handleInputChange('socialType', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    fontSize: '14px',
                    backgroundColor: '#ffffff'
                  }}
                >
                  <option value="YouTube">YouTube</option>
                  <option value="Facebook">Facebook</option>
                  <option value="TikTok">TikTok</option>
                  <option value="LinkedIn">LinkedIn</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#0f172a', marginBottom: '6px' }}>
                  Name <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="text"
                  placeholder="Enter name..."
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#0f172a', marginBottom: '6px' }}>
                Mail <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="text"
                placeholder="Enter email..."
                value={formData.mail}
                onChange={(e) => handleInputChange('mail', e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  borderRadius: '8px',
                  border: '1px solid #cbd5e1',
                  fontSize: '14px'
                }}
              />
            </div>
          </>
        )}

        {accountType === 'subscription' && (
          <>
            <div className="form-grid-two-col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#0f172a', marginBottom: '6px' }}>
                  Subscription For <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. Photoshop, Spotify..."
                  value={formData.subscriptionFor}
                  onChange={(e) => handleInputChange('subscriptionFor', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#0f172a', marginBottom: '6px' }}>
                  Name <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="text"
                  placeholder="Enter subscriber name..."
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>

            <div className="form-grid-two-col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#0f172a', marginBottom: '6px' }}>
                  Mail <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="text"
                  placeholder="Enter email..."
                  value={formData.mail}
                  onChange={(e) => handleInputChange('mail', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#0f172a', marginBottom: '6px' }}>
                  Renew Date <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="date"
                  value={formData.renewDate}
                  onChange={(e) => handleInputChange('renewDate', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>
          </>
        )}

        {/* SHARED FIELDS FOR ALL ACCOUNT TYPES ONCE SELECTED */}
        {accountType && (
          <>
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <label style={{ fontSize: '13px', fontWeight: '600', color: '#0f172a' }}>
                  Description (use for what) <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <span style={{ fontSize: '12px', color: wordCount > 350 ? '#ef4444' : '#64748b', fontWeight: wordCount > 350 ? '600' : '400' }}>
                  {wordCount} / 350 words
                </span>
              </div>
              <textarea
                rows="3"
                placeholder="Specify what this account is used for..."
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  borderRadius: '8px',
                  border: '1px solid #cbd5e1',
                  fontSize: '14px',
                  resize: 'vertical',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#0f172a', marginBottom: '6px' }}>
                Who Has <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="text"
                placeholder="Person / team who holds credentials..."
                value={formData.whoHas}
                onChange={(e) => handleInputChange('whoHas', e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  borderRadius: '8px',
                  border: '1px solid #cbd5e1',
                  fontSize: '14px'
                }}
              />
            </div>
          </>
        )}

        {/* MODAL ACTIONS FOOTER */}
        <div className="modal-form-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '16px', borderTop: '1px solid #f1f5f9' }}>
          <button
            type="button"
            className="form-btn-outlined"
            onClick={onClose}
            disabled={isSubmitting}
            style={{
              padding: '9px 18px',
              borderRadius: '8px',
              border: '1px solid #cbd5e1',
              background: '#ffffff',
              color: '#475569',
              fontWeight: '600',
              cursor: isSubmitting ? 'not-allowed' : 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="form-btn-primary"
            disabled={isSubmitting}
            style={{
              padding: '9px 24px',
              borderRadius: '8px',
              border: 'none',
              background: isSubmitting ? '#94a3b8' : '#0b66e3',
              color: '#ffffff',
              fontWeight: '600',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            {isSubmitting ? (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ animation: 'spin 1s linear infinite' }}>
                  <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                  <path d="M12 2a10 10 0 0 1 10 10" />
                </svg>
                <span>Saving...</span>
              </>
            ) : (
              <span>Save</span>
            )}
          </button>
        </div>
      </form>
    </FormModal>
  );
}

export default CreateEAccountModal;
